import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Clock } from "lucide-react";
import logoImg from "@/assets/logo.webp";

// ─── Local business structured data for SEO ───────────────────────────────────
const localBusinessSchema = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "name": "eSchool Ethiopia",
  "url": "https://eschool.et",
  "logo": "https://eschool.et/favicon_io/android-chrome-512x512.png",
  "description": "Ethiopia's #1 cloud-based school management system for schools, colleges, and educational institutions.",
  "applicationCategory": "EducationApplication",
  "operatingSystem": "Web, Android",
  "contactPoint": {
    "@type": "ContactPoint",
    "telephone": "+251966778811",
    "contactType": "customer support",
    "areaServed": "ET",
    "availableLanguage": ["Amharic", "English"],
    "hoursAvailable": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      "opens": "08:00",
      "closes": "17:00"
    }
  },
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "Around Abuna Petros, Sebera Babur, Abat Commercial Building, 6th Floor, Room 608",
    "addressLocality": "Addis Ababa",
    "addressCountry": "ET"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": "9.0222",
    "longitude": "38.7468"
  }
});

const Footer = () => {
  // Current Ethiopia time (EAT = UTC+3)
  const now = new Date();
  const eatHour = (now.getUTCHours() + 3) % 24;
  const isOpen = eatHour >= 8 && eatHour < 17 &&
    [1, 2, 3, 4, 5].includes(now.getUTCDay() === 0 ? 7 : now.getUTCDay()); // Mon–Fri

  return (
    <footer className="bg-foreground text-primary-foreground">
      {/* Inline JSON-LD structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: localBusinessSchema }}
      />

      <div className="container mx-auto px-4 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">

          {/* Brand */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <img src={logoImg} alt="eSchool Ethiopia Logo" width="300" height="125" loading="lazy" decoding="async" className="h-12 w-auto brightness-0 invert" />
            </div>
            <p className="text-sm text-primary-foreground/60 leading-relaxed mb-4">
              eSchool.et is Ethiopia's #1 cloud-based Educational Institution Management Software, built to simplify and
              digitize school operations. From fees, results, and attendance to staff, salary, library, and hostel
              management—everything is integrated in one platform. With mobile apps for students, parents, and teachers,
              it ensures seamless communication, efficiency, and transparency, making it the best choice for 1–12 grade
              schools, preschools, and private colleges across Ethiopia.
            </p>
            <p className="text-xs text-primary-foreground/70 leading-relaxed mb-4 border-l-2 border-primary-foreground/50 pl-3 italic">
              To maintain quality and dedicated support, we only onboard a maximum of{" "}
              <span className="text-sky-200 font-semibold not-italic">10 schools per year</span>, making eSchool.et the
              best choice for institutions in Ethiopia.
            </p>
            {/* Open / Closed badge */}
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${isOpen ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${isOpen ? "bg-green-400 animate-pulse" : "bg-red-400"}`} />
              {isOpen ? "Open now" : "Currently closed"}
            </span>

          </div>

          {/* Platform links */}
          <div>
            <h2 className="font-heading font-semibold mb-4 text-sm uppercase tracking-wider text-primary-foreground/70">Platform</h2>
            <ul className="space-y-2.5">
              <li><Link to="/platform" className="text-sm text-primary-foreground/60 hover:text-primary-foreground transition-colors">Platform Overview</Link></li>
              <li><Link to="/features" className="text-sm text-primary-foreground/60 hover:text-primary-foreground transition-colors">Features</Link></li>
              <li><Link to="/pricing" className="text-sm text-primary-foreground/60 hover:text-primary-foreground transition-colors">Pricing</Link></li>
              <li><Link to="/admin-dashboard" className="text-sm text-primary-foreground/60 hover:text-primary-foreground transition-colors">Admin Dashboard</Link></li>
              <li><Link to="/teacher-app" className="text-sm text-primary-foreground/60 hover:text-primary-foreground transition-colors">Teacher App</Link></li>
              <li><Link to="/parent-app" className="text-sm text-primary-foreground/60 hover:text-primary-foreground transition-colors">Parent App</Link></li>
              <li><Link to="/student-app" className="text-sm text-primary-foreground/60 hover:text-primary-foreground transition-colors">Student App</Link></li>
              <li><Link to="/school-management-in-addis-ababa" className="text-sm text-primary-foreground/60 hover:text-primary-foreground transition-colors">Addis Ababa Schools</Link></li>
              <li><Link to="/multi-institute-system-addis-ababa" className="text-sm text-primary-foreground/60 hover:text-primary-foreground transition-colors">Multi Institute System</Link></li>
            </ul>
          </div>

          {/* Company links */}
          <div>
            <h2 className="font-heading font-semibold mb-4 text-sm uppercase tracking-wider text-primary-foreground/70">Company</h2>
            <ul className="space-y-2.5">
              <li><Link to="/about" className="text-sm text-primary-foreground/60 hover:text-primary-foreground transition-colors">About eSchool</Link></li>
              <li><Link to="/contact" className="text-sm text-primary-foreground/60 hover:text-primary-foreground transition-colors">Contact Us</Link></li>
              <li><Link to="/contact" className="text-sm text-primary-foreground/60 hover:text-primary-foreground transition-colors">Request a Demo</Link></li>
              <li><Link to="/blog" className="text-sm text-primary-foreground/60 hover:text-primary-foreground transition-colors">Blog</Link></li>
              <li><Link to="/privacy-policy" className="text-sm text-primary-foreground/60 hover:text-primary-foreground transition-colors">Privacy Policy</Link></li>
              <li><Link to="/terms-and-conditions" className="text-sm text-primary-foreground/60 hover:text-primary-foreground transition-colors">Terms & Conditions</Link></li>
              <li><Link to="/sitemap" className="text-sm text-primary-foreground/60 hover:text-primary-foreground transition-colors">Sitemap</Link></li>
            </ul>
          </div>

          {/* Contact details */}
          <div>
            <h2 className="font-heading font-semibold mb-4 text-sm uppercase tracking-wider text-primary-foreground/70">Contact</h2>
            <ul className="space-y-3">
              <li>
                <a href="mailto:info@eschool.et" className="flex items-center gap-2 text-sm text-primary-foreground/60 hover:text-primary-foreground transition-colors">
                  <Mail className="w-4 h-4 shrink-0" /> info@eschool.et
                </a>
              </li>
              <li>
                <a href="tel:+251966778811" className="flex items-center gap-2 text-sm text-primary-foreground/60 hover:text-primary-foreground transition-colors">
                  <Phone className="w-4 h-4 shrink-0" /> +251 966 778 811
                </a>
              </li>
              <li>
                <div className="flex items-start gap-2 text-sm text-primary-foreground/60">
                  <Clock className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>
                    Mon–Fri: 8:00 AM – 5:00 PM<br />
                    <span className="text-primary-foreground/70 text-xs">East Africa Time (EAT, UTC+3)</span>
                  </span>
                </div>
              </li>
              <li>
                <div className="flex items-start gap-2 text-sm text-primary-foreground/60">
                  <MapPin className="w-4 h-4 shrink-0 mt-0.5" />
                  <address className="not-italic leading-relaxed">
                    Around Abuna Petros,<br />
                    Sebera Babur, Abat Commercial,<br />
                    6th Floor, Room 608<br />
                    Addis Ababa, Ethiopia
                  </address>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-primary-foreground/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-primary-foreground/70">© 2024 – 26 eSchool.et · All rights reserved.</p>
          <div className="flex items-center gap-4">
            <p className="text-sm text-primary-foreground/70">Ethiopia's Leading School Management Platform · Addis Ababa, Ethiopia</p>
            <Link to="/sitemap" className="text-sm text-primary-foreground/70 hover:text-primary-foreground underline underline-offset-2 transition-colors">Sitemap</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
